import { z } from "zod";

const monetary = z.number().min(0);
const percentage = z.number().min(0).max(100);

const expenseItemSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  amount: monetary,
  cadence: z.enum(["MONTHLY", "ANNUAL", "ONE_OFF"]),
  growthRate: z.number().optional(),
  appliesInYears: z.array(z.number().int().min(1)).optional(),
  notes: z.string().optional()
});

export const propertySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  address: z.string().min(3),
  state: z.enum(["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"]),
  kind: z.enum(["HOUSE", "UNIT", "TOWNHOUSE", "DUPLEX"]),
  notes: z.string().optional(),
  purchase: z.object({
    purchaseDate: z.string(),
    price: monetary,
    deposit: monetary,
    stampDuty: monetary,
    legalFees: monetary,
    inspections: monetary,
    lmi: monetary,
    renovationCapex: monetary
  }),
  loan: z.object({
    principal: monetary,
    interestRate: percentage,
    type: z.enum(["P_AND_I", "IO"]),
    termYears: z.number().min(1).max(40),
    ioYears: z.number().min(0).max(10),
    offsetBalance: monetary,
    annualFees: monetary
  }),
  income: z.object({
    weeklyRent: monetary,
    otherAnnualIncome: monetary,
    rentGrowthRate: percentage
  }),
  expenses: z.object({
    managementPercent: percentage,
    agentFeesAnnual: monetary,
    councilRates: monetary,
    insurance: monetary,
    maintenance: monetary,
    strata: monetary,
    water: monetary,
    utilities: monetary,
    vacancyRate: percentage,
    expenseInflationRate: percentage,
    categories: z.array(
      z.object({
        id: z.string(),
        name: z.string().min(1),
        items: z.array(expenseItemSchema)
      })
    )
  }),
  tax: z.object({
    marginalTaxRate: percentage,
    annualDepreciation: monetary,
    negativeGearingEnabled: z.boolean()
  }),
  growth: z.object({
    capitalGrowthRate: percentage,
    inflationRate: percentage,
    expenseInflationRate: percentage,
    rentGrowthRate: percentage
  }),
  attachments: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      path: z.string(),
      mimeType: z.string(),
      createdAt: z.string()
    })
  ),
  createdAt: z.string(),
  updatedAt: z.string()
});

export const scenarioSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  kind: z.enum([
    "BASELINE",
    "INTEREST_RATE_SHOCK",
    "VACANCY_SHOCK",
    "RENT_GROWTH_SHIFT",
    "SELL_IN_YEAR",
    "EXTRA_REPAYMENT"
  ]),
  interestRateDelta: z.number().optional(),
  vacancyDelta: z.number().optional(),
  rentGrowthDelta: z.number().optional(),
  sellYear: z.number().optional(),
  extraRepaymentMonthly: z.number().optional()
});

export const portfolioSchema = z.object({
  id: z.string(),
  name: z.string(),
  assumptions: z.object({
    forecastYears: z.number().min(1).max(40),
    defaultCapitalGrowthRate: percentage,
    defaultRentGrowthRate: percentage,
    defaultExpenseInflationRate: percentage,
    defaultInflationRate: percentage
  }),
  properties: z.array(propertySchema),
  createdAt: z.string(),
  updatedAt: z.string()
});

export type PortfolioInput = z.infer<typeof portfolioSchema>;
